import styles from 'css/src/components/public-pages/TemplatePreviewsSection.module.scss';

import React from 'react';
import classNames from 'classnames';

const baseUrl =
    'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/videos/TemplatePreviews/v4/';

type TemplatePreview = {
    image: string;
    video: string;
    aspectRatio: 'square' | 'landscape' | 'vertical';
};

/**
 * Videos were shuffled manually and placed in this array.
 * Each file was exported in webm using ffmpeg from the original mp4 files created by CST.
 * The webp images were exported from the same source files, with a 1 second seek (-ss 1).
 */
const defaultTemplatePreviews = [
    {
        image: 'prev_memory_land_v1_thumb.webp',
        video: 'prev_memory_land_v1.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'helios_prev_land_thumb.webp',
        video: 'helios_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'surge_prev_land_thumb.webp',
        video: 'surge_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'photon_prev_square_thumb.webp',
        video: 'photon_prev_square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'shamrock_prev_land_thumb.webp',
        video: 'shamrock_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'helios_prev_square_thumb.webp',
        video: 'helios_prev_square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'photon_prev_land_thumb.webp',
        video: 'photon_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'photon_prev_vert_thumb.webp',
        video: 'photon_prev_vert.webm',
        aspectRatio: 'vertical' as const,
    },
    {
        image: 'oasis_prev_land_thumb.webp',
        video: 'oasis_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'surge_prev_vert_thumb.webp',
        video: 'surge_prev_vert.webm',
        aspectRatio: 'vertical' as const,
    },
    {
        image: 'assembly_prev_square_thumb.webp',
        video: 'assembly_prev_square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'oasis_prev_vert_thumb.webp',
        video: 'oasis_prev_vert.webm',
        aspectRatio: 'vertical' as const,
    },
    {
        image: 'oasis_prev_square_thumb.webp',
        video: 'oasis_prev_square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'surge_prev_square_thumb.webp',
        video: 'surge_prev_square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'prev_foresight_vert_thumb.webp',
        video: 'prev_foresight_vert.webm',
        aspectRatio: 'vertical' as const,
    },
    {
        image: 'prev_foresight_square_thumb.webp',
        video: 'prev_foresight_square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'shamrock_prev_vert_thumb.webp',
        video: 'shamrock_prev_vert.webm',
        aspectRatio: 'vertical' as const,
    },
    {
        image: 'assembly_prev_land_thumb.webp',
        video: 'assembly_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
].map((item) => ({
    ...item,
    image: `${baseUrl}thumbnails/${item.image}`,
    video: `${baseUrl}${item.video}`,
}));

const enterpriseBaseUrl =
    'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/TemplatePreviews/';

export const enterpriseTemplatePreviews = [
    {
        image: 'dnv_preview_thumb.webp',
        video: 'dnv_preview.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'emerson_marketing_v2_preview_thumb.webp',
        video: 'emerson_marketing_v2_preview.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'mfa_preview_land_v2_thumb.webp',
        video: 'mfa_preview_land_v2.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'mitsubishi_prev_land_thumb.webp',
        video: 'mitsubishi_prev_land.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'prev_bestwestern_thumb.webp',
        video: 'prev_bestwestern.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'prev_siemensenergy_landscape_thumb.webp',
        video: 'prev_siemensenergy_landscape.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'swissre_preview_hd_thumb.webp',
        video: 'swissre_preview_hd.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'act_preview_l_thumb.webp',
        video: 'act_preview_l.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'act_preview_v_thumb.webp',
        video: 'act_preview_v.webm',
        aspectRatio: 'vertical' as const,
    },
    {
        image: 'cisco_2024_sq_thumb.webp',
        video: 'cisco_2024_sq.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'colliers_preview_l_2_thumb.webp',
        video: 'colliers_preview_l.webm',
        aspectRatio: 'landscape' as const,
    },
    {
        image: 'emerson_marketing_v2_preview___square_thumb.webp',
        video: 'emerson_marketing_v2_preview___square.webm',
        aspectRatio: 'square' as const,
    },
    {
        image: 'emerson_marketing_v2_preview___vertical_thumb.webp',
        video: 'emerson_marketing_v2_preview___vertical.webm',
        aspectRatio: 'vertical' as const,
    },
].map((item) => ({
    ...item,
    image: `${enterpriseBaseUrl}thumbnails/${item.image}`,
    video: `${enterpriseBaseUrl}${item.video}`,
}));

/**
 * This component repeats template previews 3 times horizontally and animates X position while looping
 * (using % totalWidth).
 * We use a pausable timer class to be able to pause previews.
 */

const speed = 0.05;
const margin = 5;
const REPEATS = 4;

class PausableDateTimer {
    private pauseStart: number | null = null;
    private pauseOffset = 0;

    constructor() {
        this.play();
    }

    getTime() {
        if (this.pauseStart) {
            return this.pauseStart;
        }
        return new Date().getTime() - this.pauseOffset;
    }

    pause() {
        if (this.pauseStart !== null) {
            return;
        }
        this.pauseStart = new Date().getTime() - this.pauseOffset;
    }

    play() {
        if (this.pauseStart) {
            const now = new Date().getTime();
            this.pauseOffset = now - this.pauseStart;
            this.pauseStart = null;
        }
    }
}

const TemplatePreviewImage = React.memo(function TemplatePreviewImage({
    preview,
    style,
}: {
    preview: TemplatePreview;
    style?: React.CSSProperties;
}) {
    const ref = React.useRef<HTMLDivElement>(null);
    const imageUrl = preview.image;
    const videoUrl = preview.video;

    const [isHovered, setIsHovered] = React.useState(false);

    React.useEffect(() => {
        const currentRef = ref.current;
        if (currentRef) {
            const handleMouseEnter = () => setIsHovered(true);
            const handleMouseLeave = () => setIsHovered(false);

            currentRef.addEventListener('mouseenter', handleMouseEnter);
            currentRef.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                currentRef.removeEventListener('mouseenter', handleMouseEnter);
                currentRef.removeEventListener('mouseleave', handleMouseLeave);
            };
        }
    }, []);

    return (
        <div
            className={classNames(
                styles['template-preview'],
                {
                    landscape: styles['template-preview-landscape'],
                    square: styles['template-preview-square'],
                    vertical: styles['template-preview-vertical'],
                }[preview.aspectRatio],
            )}
            ref={ref}
            style={style}
        >
            {isHovered && (
                <video
                    className={styles['template-preview-video']}
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={imageUrl}
                    preload="none"
                >
                    <source src={videoUrl} type="video/webm" />
                </video>
            )}
            <img
                className={styles['template-preview-image']}
                src={imageUrl}
                loading="lazy"
                decoding="async"
                alt="Video template preview"
            />
        </div>
    );
});

const TemplatePreviewsRow = React.memo(function TemplatePreviewsRow({
    previews,
    direction = 1,
    className,
}: {
    previews: TemplatePreview[];
    direction?: number;
    className?: string;
}) {
    const timer = React.useRef(new PausableDateTimer());
    const ref = React.useRef<HTMLDivElement>(null);
    const [offsetX, setOffsetX] = React.useState(0);

    // We dinamically repeat the previews, but we
    // cache it for performance to avoid re-renders of TemplatePreviewImage
    const repeatedPreviews = React.useMemo(() => {
        const repeated = [];
        for (let i = 0; i < REPEATS; i++) {
            repeated.push(...previews);
        }
        return repeated;
    }, [previews]);

    React.useEffect(() => {
        let frameId: number;

        const animationFrame = () => {
            if (!ref.current) return;
            const items = Array.from(ref.current.children);
            const widths = items.slice(0, items.length).map((item: Element) => item.clientWidth);
            // Note that we account for left + right margin
            const totalWidth = widths.reduce((acc, width) => acc + width + margin * 2, 0);

            const time = timer.current.getTime();
            const repeatWidth = totalWidth / REPEATS;

            setOffsetX(((direction * time * speed) % repeatWidth) - repeatWidth);

            frameId = requestAnimationFrame(animationFrame);
        };

        frameId = requestAnimationFrame(animationFrame);

        return () => {
            cancelAnimationFrame(frameId);
        };
    });

    React.useEffect(() => {
        const handleMouseEnter = () => {
            timer.current.pause();
        };
        const handleMouseLeave = () => {
            timer.current.play();
        };
        const currentRef = ref.current;
        if (currentRef) {
            currentRef.addEventListener('mouseenter', handleMouseEnter);
            currentRef.addEventListener('mouseleave', handleMouseLeave);
        }
        return () => {
            if (currentRef) {
                currentRef.removeEventListener('mouseenter', handleMouseEnter);
                currentRef.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div className={classNames(styles['template-preview-row'], className)} ref={ref}>
            {repeatedPreviews.map((preview, index) => (
                <TemplatePreviewImage
                    key={index}
                    preview={preview}
                    style={{
                        transform: `translateX(${offsetX.toFixed(1)}px)`,
                        marginRight: `${margin}px`,
                        marginLeft: `${margin}px`,
                    }}
                />
            ))}
        </div>
    );
});

const TemplatePreviewsSection = React.memo(function TemplatePreviewsSection({
    rows,
    templatePreviews,
}: {
    rows?: number;
    templatePreviews?: TemplatePreview[];
}) {
    rows = rows || 3;
    templatePreviews = templatePreviews || defaultTemplatePreviews;

    const templatePreviewRows = React.useMemo(() => {
        const groupSize = Math.ceil(templatePreviews.length / (rows || 1));
        const templatePreviewRows: TemplatePreview[][] = [];
        templatePreviews.forEach((preview, index) => {
            if (index % groupSize === 0) {
                templatePreviewRows.push([]);
            }
            templatePreviewRows[templatePreviewRows.length - 1].push(preview);
        });

        return templatePreviewRows;
    }, [templatePreviews, rows]);

    return (
        <div
            className={`p-l5-16 p-l5-sm-48 py-l5-lg-32 ${styles['template-previews-section']} pb-l5-48`}
        >
            <div className="container">
                {templatePreviewRows.map((previews, index) => (
                    <TemplatePreviewsRow
                        key={index}
                        previews={previews}
                        direction={index % 2 === 0 ? 1 : -1}
                        className={index >= 2 ? styles['template-preview-row-overflow'] : undefined}
                    />
                ))}
            </div>
        </div>
    );
});

export default TemplatePreviewsSection;
